import {ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {filter, Observable, Subscription, take} from "rxjs";
import {PopupService} from "../../../services/popup.service";
import {AuthService} from "../../../services/auth.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../../state/app.state";
import {SharedService} from "../../../services/shared.service";
import {selectAllSearchesFailure, selectAllSearchesSuccess, selectArchiveReportFailure, selectArchiveReportSuccess, selectLoading, selectRestoreReportFailure, selectRestoreReportSuccess} from "../../../state/search/search.selectors";
import {EventTypes} from "../../../event-types";
import {allSearches, newSearch, archiveReport, restoreReport} from "../../../state/search/search.actions";
import {map} from "rxjs/operators";
import {updateIsLoading} from "../../../state/search/search.actions";
import {selectAreAllEventsComplete} from "../../../state/search/search.selectors";
import { ReportListItem } from 'src/app/interfaces/report-list-item.interface'
import {HeaderService} from "../../../services/header.service";
import {SidebarService} from "../../../services/sidebar.service";
import {EchoService} from "../../../services/echo.service";
import {updateReportStatusInSearches} from "../../../state/search/search.actions";
import { Workspace } from '../../../models/workspace.model';
import * as SearchExportActions from '../../../state/searchExport/search-export.actions';
import * as SearchExportSelectors from '../../../state/searchExport/search-export.selectors';
import { SearchPersonComponent } from '../../../common/search-person/search-person.component';

@Component({
  selector: 'app-workspace-reports',
  templateUrl: './workspace-reports.component.html',
  styleUrl: './workspace-reports.component.scss'
})
export class WorkspaceReportsComponent implements OnInit, OnDestroy {

  @ViewChild('inputElement') inputElement: ElementRef;
  @ViewChild('personSearch') personSearch: SearchPersonComponent;
  @Input() workspaceId: string;
  @Input() workspace: Workspace; // Add workspace input

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      this.inputElement.nativeElement.focus();
      event.preventDefault();  // Prevent any default action
    }
  }

  mediaQuery: MediaQueryList;

  pagination: any = {
    current_page: 1,
    from: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  }

  perPage: number = 10;

  errorMessage = ""
  linkedin = ""

  filteredList$: Observable<any[]>;
  private processedReports: ReportListItem[] = [];

  loading: boolean = false;
  range:string = '10';
  archivedReports: boolean = false;

  searchQuery: string = '';

  // Archive confirmation modal state
  showArchiveConfirm: boolean = false;
  pendingArchiveId: number | null = null;
  pendingArchiveName: string = '';

  user: any;
  showTooltip:[boolean] = [false];
  showArchivedSearchesTooltip:boolean = false;
  showGoBackToSearchesTooltip:boolean = false;
  windowHeight: number = 50;

  newSearchSuccessSubscription: Subscription = null;
  newSearchFailureSubscription: Subscription = null;

  allSearchesSuccessSubscription: Subscription = null;
  allSearchesFailureSubscription: Subscription = null;

  archiveSearchSuccessSubscription: Subscription = null;
  archiveSearchFailureSubscription: Subscription = null;

  restoreSearchSuccessSubscription: Subscription = null;
  restoreSearchFailureSubscription: Subscription = null;

  selectedFilters: { [key: string]: string[] } | null = null;

  exporting: boolean = false;
  exportLoadingSubscription: Subscription = null;

  imageErrors: { [key: string]: boolean } = {};

  isMobile: boolean = false;

  private isIOS: boolean = /iPad|iPhone|iPod/.test(navigator.userAgent);

  isDesktop = window.innerWidth >= 1024;
  showMobileSidebar = false;

  showOverlay = false;

  // Remove the loading flag since we're fixing the root cause

  constructor(
      private popupService: PopupService,
      private authService : AuthService,
      private routerService: Router,
      private route: ActivatedRoute,
      private store: Store<AppState>,
      private sharedService: SharedService,
      private headerService: HeaderService,
      private sidebarService: SidebarService,
      private echoService: EchoService,
      private cdr: ChangeDetectorRef
  ) {
    this.mediaQuery = window.matchMedia('(max-width: 768px)');
  }

  ngOnInit(): void {
    // Show header with custom title - Modified for workspace context
    this.headerService.setHeaderState('Workspace Reports', null, false);

    if (this.isIOS) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
    }

    this.detectMobile()

    // Restore page / per-page / search / archived state from the URL so that
    // navigating into a report and pressing back (or refreshing the page) keeps
    // the user where they were. The URL is the single source of truth — see
    // syncStateToUrl().
    const restoredPage = this.restoreStateFromUrl();

    // Subscribe to the loading state from the store
    this.store.select(selectLoading).subscribe((loading) => {
      this.loading = loading;
    });

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

    if (this.user.has_completed_setup === 0) {
      // User setup handling
    } else {
      this.allSearches(restoredPage, this.perPage, this.searchQuery, this.archivedReports)
    }

    this.sharedService.eventEmitter.subscribe(event => {
      if (event.type === EventTypes.RELOAD_REPORTS) {
        this.allSearches(this.pagination.current_page, this.perPage, this.searchQuery, this.archivedReports)
      }
    });

    this.updateHeight();

    this.getFilteredList()

    // Listen for report finished event
    this.sharedService.eventEmitter.subscribe((event) => {
      const eventType = event.type;

      if (eventType === 'REPORT_FINISHED') {
        this.allSearches(this.pagination.current_page, this.perPage, this.searchQuery, this.archivedReports)
      }
    });

    this.checkIfDesktop()

    // Subscribe to export loading state
    this.exportLoadingSubscription = this.store.select(SearchExportSelectors.selectStartExportLoading)
      .subscribe(loading => {
        this.exporting = loading;
      });

    this.echoService.getEcho().private('ReportChannel.' + this.user.id)
        .listen('.search-report-completed', (reportEvent: any) => {
          // Convert the report ID to a number
          const reportId = Number(reportEvent.id || reportEvent.search_id);

          if (isNaN(reportId)) {
            return;
          }

          // Dispatch action with the numeric ID
          this.store.dispatch(updateReportStatusInSearches({
            reportId: reportId,
            statusId: 3
          }));
        });

  }

  checkIfDesktop(): void {
    this.isDesktop = window.innerWidth >= 1024;
  }

  onDropdownVisibility(isVisible: boolean) {
    this.showOverlay = isVisible;
  }

  /**
   * The overlay shown while the person-search dropdown is open swallows clicks on the
   * report list, so clicking it must dismiss the dropdown rather than do nothing.
   */
  dismissPersonSearch(): void {
    this.personSearch?.closeDropdown();
    this.showOverlay = false;
  }

  getFilteredList() {

    // Listen for all events completion for all reports
    this.filteredList$ = this.store.select(selectAllSearchesSuccess).pipe(
        filter((response) => response !== null),
        map((response) => {
          const reports = response.data || [];

          // Monitor event statuses for each report
          reports.forEach((report) => {
            this.store
                .select(selectAreAllEventsComplete(report.id))
                .pipe(take(1)) // Automatically unsubscribe after the first value
                .subscribe((allComplete) => {
                  if (allComplete) {
                    // Update the `is_loading` property for the report
                    this.store.dispatch(
                        updateIsLoading({ searchId: report.id, isLoading: false })
                    );
                  }
                });
          });

          return reports;
        })
    );

  }

  // Modify the onFiltersChanged method to update selected filters
  onFiltersChanged(filters: { [key: string]: string[] }): void {
    this.selectedFilters = filters;
    this.allSearches(); // Call the search with the updated filters
  }

  /**
   * All searches - Modified to filter by workspaceId
   */
  allSearches(page: number = 1, perPage: number = 10, search: string = '',archived: boolean = false): void {
    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
    }
    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
    }

    // Include workspaceId in the dispatch
    this.store.dispatch(allSearches({
      page: page,
      perPage: perPage,
      search: search,
      tags: this.selectedFilters !== null ? this.selectedFilters : undefined,
      archived:archived,
      workspaceId: this.workspaceId // Add workspace filtering
    }));

    // Listen for the all searches success or failure from the store
    const allSearchesSuccess$ = this.store.select(selectAllSearchesSuccess);
    const allSearchesFailure$ = this.store.select(selectAllSearchesFailure);

    if (this.allSearchesSuccessSubscription) {
      this.allSearchesSuccessSubscription.unsubscribe();
    }

    // Subscribe to the all searches success
    this.allSearchesSuccessSubscription = allSearchesSuccess$.pipe(
        filter(response => response !== null)
    ).subscribe({
      next: (response) => {
        // Handle success
        const reports = response.data || [];
        if (reports) {

          // Update processed reports and filteredList$
          this.processedReports = (response.data || []).map((report: any) => ({
            id: report.id,
            name: report.name || 'Unknown',
            title: report.search_person?.enhanced_data?.title,
            picture: report.search_person?.profile_picture || 'https://ui-avatars.com/api/?background=8b5cf6&color=fff&rounded=true&name='+report.name+'&format=svg&size=128',
            country: report.country || null,
            is_archived: report.is_archived || false,
            company: report.company ? {
              name: report.company.name || 'Unknown Company',
              logo_url: report.company.enhanced_data?.logo_url || null,
              city: report.company.enhanced_data?.city || null,
              country: report.company.enhanced_data?.country || null,
              icp_score: report.company.icp_score || 0,
            } : null,
            updated_at_human: report.updated_at_human || 'Unknown Date',
            search_status_id: report.search_status_id
          }));

          this.filteredList$ = new Observable((observer) => {
            observer.next(this.processedReports);
            observer.complete();
          });

          // Update pagination metadata
          this.pagination = {
            current_page: response.current_page,
            total: response.total,
            last_page: response.last_page,
            per_page: response.per_page,
            from: response.from,
            to: response.to
          };

        }
      },
      error: () => {
        if (this.allSearchesSuccessSubscription) {
          this.allSearchesSuccessSubscription.unsubscribe();
        }
      }
    });

    // Subscribe to the search failure
    this.allSearchesFailureSubscription = allSearchesFailure$.pipe(
        filter(failure => failure !== null),
        take(1)
    ).subscribe({
      next: (error) => {
        // Handle failure
        const errorMessage = error.error.message;

        if (this.allSearchesFailureSubscription) {
          this.allSearchesFailureSubscription.unsubscribe();
        }
      },
      error: () => {
        if (this.allSearchesFailureSubscription) {
          this.allSearchesFailureSubscription.unsubscribe();
        }
      }
    });
  }

  ngOnDestroy(): void {

    if (this.isIOS) {
      document.body.style.overflow = '';
      document.body.style.position = '';
    }

    if (this.newSearchSuccessSubscription) {
      this.newSearchSuccessSubscription.unsubscribe();
    }
    if (this.newSearchFailureSubscription) {
      this.newSearchFailureSubscription.unsubscribe();
    }

    if (this.allSearchesSuccessSubscription) {
      this.allSearchesSuccessSubscription.unsubscribe();
    }
    if (this.allSearchesFailureSubscription) {
      this.allSearchesFailureSubscription.unsubscribe();
    }
    if (this.exportLoadingSubscription) {
      this.exportLoadingSubscription.unsubscribe();
    }
  }

  exportReports(): void {
    const workspaceId = this.workspaceId ? Number(this.workspaceId) : undefined;
    this.store.dispatch(SearchExportActions.startSearchExport({
      search: this.searchQuery || undefined,
      tags: this.selectedFilters,
      archived: this.archivedReports,
      workspace_id: workspaceId
    }));
  }

  @HostListener('touchmove', ['$event'])
  disableTouch(event: TouchEvent) {
    if (this.isIOS) {
      const isInsideUl = (event.target as HTMLElement).closest('ul');
      if (!isInsideUl) {
        event.preventDefault(); // Prevent drag/scroll outside ul
      }
    }
  }

  /**
   * Page change.
   * This function is triggered when the user changes the page.
   */
  onPageChanged(page: number): void {
    this.allSearches(page, this.perPage, this.searchQuery, this.archivedReports)
    this.syncStateToUrl(page)
  }

  /**
   * Per page change.
   * This function is triggered when the user changes the per page.
   */
  onPerPageChanged(perPage: number): void {

    this.perPage = perPage

    this.pagination.per_page = perPage;
    this.pagination.current_page = 1;
    this.allSearches(1, perPage, this.searchQuery, this.archivedReports);
    this.syncStateToUrl(1);

  }

  viewArchivedReports() {
    this.archivedReports = true;
    this.searchQuery = ''
    this.allSearches(1, this.perPage, '', true);
    this.syncStateToUrl(1);
    return true;
  }

  restoreSearch(reportId,event) {

    event.stopPropagation();

    this.store.dispatch(restoreReport({
      reportId:reportId
    }));

    // Listen for the all searches success or failure from the store
    const restoreSearchSuccess$ = this.store.select(selectRestoreReportSuccess);
    const restoreSearchFailure$ = this.store.select(selectRestoreReportFailure);

    if (this.restoreSearchSuccessSubscription) {
      this.restoreSearchSuccessSubscription.unsubscribe();
    }

    // Subscribe to the all searches success
    this.restoreSearchSuccessSubscription = restoreSearchSuccess$.pipe(
        filter(response => response !== null),
        take(1)
    ).subscribe({
      next: (response) => {
        this.popupService.notify('Search restored Successfully!', 'Restored search', 'success');
        this.reloadAfterMutation();
      },
      error: (error) => {
        // Handle error

        if (this.restoreSearchSuccessSubscription) {
          this.restoreSearchSuccessSubscription.unsubscribe();
        }
      }
    });

    // Subscribe to the search failure
    this.restoreSearchFailureSubscription = restoreSearchFailure$.pipe(
        filter(failure => failure !== null),
        take(1)
    ).subscribe({
      next: (error) => {
        // Handle failure
        const errorMessage = error.error.message;
        this.popupService.notify(errorMessage, 'Error', 'error');

        if (this.restoreSearchFailureSubscription) {
          this.restoreSearchFailureSubscription.unsubscribe();
        }
      },
      error: (error) => {
        // Handle error in error handling

        if (this.restoreSearchFailureSubscription) {
          this.restoreSearchFailureSubscription.unsubscribe();
        }
      }
    });

  }

  /**
   * Archive button clicked — open the confirmation modal instead of archiving
   * immediately. Stops propagation so the row's link does not navigate.
   */
  promptArchive(reportId: number, name: string, event: Event): void {
    event.stopPropagation();
    this.pendingArchiveId = reportId;
    this.pendingArchiveName = name || '';
    this.showArchiveConfirm = true;
  }

  /** User dismissed the archive confirmation. */
  cancelArchive(): void {
    this.showArchiveConfirm = false;
    this.pendingArchiveId = null;
    this.pendingArchiveName = '';
  }

  /** User confirmed — archive the pending report. */
  confirmArchive(): void {
    const reportId = this.pendingArchiveId;
    this.showArchiveConfirm = false;
    this.pendingArchiveName = '';
    this.pendingArchiveId = null;

    if (reportId != null) {
      this.archiveSearch(reportId);
    }
  }

  archiveSearch(reportId) {
    this.store.dispatch(archiveReport({
      reportId:reportId
    }));

    // Listen for the all searches success or failure from the store
    const archiveSearchSuccess$ = this.store.select(selectArchiveReportSuccess);
    const archiveSearchFailure$ = this.store.select(selectArchiveReportFailure);

    if (this.archiveSearchSuccessSubscription) {
      this.archiveSearchSuccessSubscription.unsubscribe();
    }

    // Subscribe to the all searches success
    this.archiveSearchSuccessSubscription = archiveSearchSuccess$.pipe(
        filter(response => response !== null),
        take(1)
    ).subscribe({
      next: (response) => {
        this.popupService.notify('Search archived Successfully!', 'Archived search', 'success');
        this.reloadAfterMutation();
      },
      error: (error) => {
        // Handle error
        this.reloadAfterMutation();

        if (this.archiveSearchSuccessSubscription) {
          this.archiveSearchSuccessSubscription.unsubscribe();
        }
      }
    });

    // Subscribe to the search failure
    this.archiveSearchFailureSubscription = archiveSearchFailure$.pipe(
        filter(failure => failure !== null),
        take(1)
    ).subscribe({
      next: (error) => {
        // Handle failure
        const errorMessage = error.error.message;
        this.popupService.notify(errorMessage, 'Error', 'error');

        if (this.archiveSearchFailureSubscription) {
          this.archiveSearchFailureSubscription.unsubscribe();
        }
      },
      error: (error) => {
        // Handle error in error handling

        if (this.archiveSearchFailureSubscription) {
          this.archiveSearchFailureSubscription.unsubscribe();
        }
      }
    });

  }

  /**
   * Refresh reports.
   * This function is triggered when the user clicks the 'Refresh' button.
   */
  refreshReports () {
    this.archivedReports = false;
    this.searchQuery = ''
    this.allSearches(1, this.perPage, '', false)
    this.syncStateToUrl(1)
  }

  /**
   * Reload the list after archiving/restoring a report, keeping the user on the
   * same page (and same per-page / search / archived filters) instead of jumping
   * back to page 1. If the mutated report was the last item on the current page,
   * step back one page so we don't land on an empty page.
   */
  private reloadAfterMutation(): void {
    let page = this.pagination?.current_page || 1;
    if (page > 1 && this.processedReports.length <= 1) {
      page = page - 1;
    }
    this.allSearches(page, this.perPage, this.searchQuery, this.archivedReports);
    this.syncStateToUrl(page);
  }

  /**
   * Search report.
   * This function is triggered when the user clicks the 'Search' button.
   */
  searchReport () {
    this.allSearches(1, this.perPage, this.searchQuery, this.archivedReports)
    this.syncStateToUrl(1)
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.updateHeight();

    const wasDesktop = this.isDesktop;
    this.checkIfDesktop();
  }

  updateHeight() {
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const filterHeight = 52;
    this.windowHeight = window.innerHeight - (22.2 * remSize) - filterHeight + 12;
  }

  onCompanyImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  onImageError(reportId: number) {
    this.imageErrors[reportId] = true;
  }

  private detectMobile() {
    // Example mobile detection - adjust breakpoint as needed
    this.isMobile = window.innerWidth < 768;
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
    });
  }

  toggleMobileSidebar(): void {
    this.sidebarService.toggleMobileSidebar();
  }

  onWorkspaceChanged(workspace: Workspace): void {
    this.workspaceId = workspace.id;
    this.workspace = workspace;

    this.selectedFilters = null;
    this.searchQuery = '';
    this.archivedReports = false;

    this.allSearches(1, this.perPage, '', false);
    // Navigate without query params so the new workspace starts on a clean page 1.
    this.routerService.navigate(['/workspaces', workspace.id]);
  }

  /**
   * Restore page / per-page / search / archived state from the route query
   * params. Returns the page to load (1 when none is present). Keeping this in
   * the URL is what lets the list survive a round-trip into a report, a browser
   * refresh, and makes the view shareable.
   */
  private restoreStateFromUrl(): number {
    const qp = this.route.snapshot.queryParamMap;
    const page = Number(qp.get('page'));
    const perPage = Number(qp.get('per_page'));

    if (perPage > 0) {
      this.perPage = perPage;
    }
    this.searchQuery = qp.get('search') ?? '';
    this.archivedReports = qp.get('archived') === 'true';

    return page > 0 ? page : 1;
  }

  /**
   * Mirror the current list state into the URL query params, replacing the
   * current history entry (so paging doesn't pollute the back stack). We build
   * the params explicitly and replace the whole query string (rather than
   * 'merge') so stale page/search/archived values can never linger. Only
   * non-default values are included, keeping the URL clean for the common case
   * (page 1, 10 per page, no search, not archived). This route owns no other
   * query params, so a full replace is safe.
   */
  private syncStateToUrl(page: number): void {
    const queryParams: { [key: string]: string | number } = {};

    if (page > 1) {
      queryParams['page'] = page;
    }
    if (this.perPage !== 10) {
      queryParams['per_page'] = this.perPage;
    }
    if (this.searchQuery) {
      queryParams['search'] = this.searchQuery;
    }
    if (this.archivedReports) {
      queryParams['archived'] = 'true';
    }

    if (!this.workspaceId) {
      return;
    }

    // Navigate to the explicit /workspaces/:id path (not relativeTo + []) so the
    // :id can never be dropped — an id-less /workspaces URL makes WorkspaceComponent
    // redirect to the workspace picker. Same :id ⇒ paramMap doesn't re-emit, so no reload.
    this.routerService.navigate(['/workspaces', this.workspaceId], {
      queryParams,
      replaceUrl: true,
    });
  }

}
